import { useEffect, useState } from 'react';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { TGetOffersResponse } from 'lendingApi/lapiOverrides.types';
import { useNavigate } from 'react-router-dom';
import { TSignupErrorType } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useGetOffers } from '../../../lendingApi/lendingApiHooks';
import {
  getAutoBankStatements,
  getDataRequirementErrors,
  getSuggestedOffer,
  hasRequiredDocuments,
  SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES,
  SIGNUP_ERROR_CODES,
} from '../helpers/useOffers.helpers';
import { useRouterApplicationId } from './useRouterApplicationId';
import { useSignupStep } from './useSignupStep';

type TProps = { getOffersEnabled: boolean };

export const useOffers = ({ getOffersEnabled }: TProps) => {
  const { isUK } = useLocale();
  const { updateLatestAllowedStep, state, stateFetching } = useSignupStep();
  const [isDeciding, setIsDeciding] = useState(false);
  const [signupError, setSignupError] = useState<TSignupErrorType>();
  const [isErrorChecked, setIsErrorChecked] = useState(false);
  const [selectOfferError, setSelectOfferError] = useState(false);
  const [hasSignupDecision, setHasSignupDecision] = useState(false);
  const [isSecurityCheckRequired, setIsSecurityCheckRequired] = useState(false);
  const { splitTests } = useIntroducerData();
  const navigate = useNavigate();
  const applicationId = useRouterApplicationId();
  const { trackEvent } = useAmplitude();

  const isReintroduction = Boolean(
    state?.ui?.reintroduction &&
    splitTests?.same_introducer_account_reintroductions,
  );

  const {
    data: offersData,
    error: offersError,
    refetch: refetchOffersData,
    isRefetching: isRefetchingOffers,
    isLoading: isLoadingOffers,
  } = useGetOffers(getOffersEnabled, applicationId as string);

  const offers = offersData?.data?.offers;
  const suggestedOffer = getSuggestedOffer(offers);
  const dataRequirementErrors = getDataRequirementErrors(offers);

  const suggestedOfferRequirements = suggestedOffer?.data_requirements?.find(
    (requirement) => requirement.category === 'documents',
  )?.actions;

  const documentRequirements =
    suggestedOfferRequirements && suggestedOfferRequirements?.length > 0
      ? suggestedOfferRequirements
      : offers
          ?.find(
            (offer) =>
              offer.offer_type === 'indicative' ||
              offer.offer_type === 'sample',
          )
          ?.data_requirements?.find(
            (requirement) => requirement.category === 'documents',
          )?.actions;

  const hasIndicativeOffers = Boolean(
    offers?.some((offer) => offer.offer_type === 'indicative'),
  );

  const hasSampleOffers = Boolean(
    offers?.some((offer) => offer.offer_type === 'sample'),
  );

  const shouldRedirect = (() => {
    if (!offers || signupError || isSecurityCheckRequired) return false;

    const isSampleOffer = suggestedOffer?.offer_type === 'sample';
    const hasSelfServeErrorCode = dataRequirementErrors?.some((error) =>
      SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(error.code),
    );
    return Boolean(
      isSampleOffer || (hasIndicativeOffers && hasSelfServeErrorCode),
    );
  })();

  const goToApplication = () => {
    navigate(
      PATH_NAMES.application({ applicationId: applicationId as string }),
    );
  };

  const goToDocuments = async () => {
    await updateLatestAllowedStep('new/documents');
    navigate(PATH_NAMES.newApplicationDocuments({ applicationId }), {
      replace: true,
    });
  };

  // Set error to show on error message screen
  const handleSignupError = (namedError?: TSignupErrorType) => {
    setSignupError(namedError ?? 'technical');
    setIsErrorChecked(true);
  };

  const handleSetSignupErrors = (
    offersList: TGetOffersResponse['data']['offers'],
  ) => {
    setSignupError(undefined);
    setIsSecurityCheckRequired(false);

    if (!applicationId) return handleSignupError();

    const errors = getDataRequirementErrors(offersList);
    if (!errors?.length) return setIsErrorChecked(true);

    const hasAdditionalContacts =
      state?.application?.people && state.application.people.length > 1;

    // handle tracking separately so all errors get logged before returns
    errors.forEach((error) => {
      trackEvent(AMPLITUDE_EVENTS.offersErrored, {
        'Error code': error.code,
        'Method type': error.type,
        'Error detail': error.detail,
        Reintroduction: isReintroduction,
      });
    });

    for (const { code } of errors) {
      if (
        // if the error is resolveable, dont set a signup error
        SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(code) &&
        !hasAdditionalContacts
      ) {
        setIsErrorChecked(true);
        return;
      }

      //  credit_check and confirm_details errors will only be set if the application does not fall into the above block (if it has multiple contacts)
      if (SIGNUP_ERROR_CODES.CREDIT_CHECK.includes(code)) {
        return handleSignupError('credit_check');
      }

      if (SIGNUP_ERROR_CODES.CONFIRM_APPLICANT_DETAILS.includes(code)) {
        return handleSignupError('confirm_details');
      }

      if (SIGNUP_ERROR_CODES.DUPLICANT_GUARANTOR.includes(code)) {
        return handleSignupError('duplicate_guarantor');
      }

      // Flagging enhanced security check for the resolving linked accounts error
      if (code === '150') {
        return setIsSecurityCheckRequired(true);
      }

      return handleSignupError();
    }

    setIsErrorChecked(true);
  };

  /**
   * Handles user redirection (called when product offers page loads):
   *  - Resolvable data requirement errors → go to check Details page (UK only)
   *  - No required documents → proceed to Application page
   *  - Indicative offers → go to Trading History page
   *  - Otherwise (Required documents) → go to Trading History page
   */
  const handleRedirect = async (
    offersList: TGetOffersResponse['data']['offers'],
  ) => {
    const hasResolveableError = getDataRequirementErrors(offersList)?.some(
      (error) => SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(error.code),
    );

    if (hasResolveableError && isUK) {
      return navigate(PATH_NAMES.newApplicationCheckDetails({ applicationId }));
    }

    if (hasIndicativeOffers) {
      return goToDocuments();
    }

    if (!hasRequiredDocuments(getSuggestedOffer(offersList))) {
      const nonSuggestedOfferRequirements = offers
        ?.find(
          (offer) =>
            offer.offer_type === 'indicative' || offer.offer_type === 'sample',
        )
        ?.data_requirements?.find(
          (requirement) => requirement.category === 'documents',
        )?.actions;

      if (!nonSuggestedOfferRequirements || !isUK) {
        return goToApplication();
      }
    }
    await goToDocuments();
    setHasSignupDecision(true);
  };

  // Refetch the offers data, check that all required docs are uploaded, and proceed to the application page
  const handleDecision = async () => {
    setIsDeciding(true);
    try {
      const refetchedOffersData = (await refetchOffersData())?.data?.data;
      if (!refetchedOffersData?.offers) return null;

      if (
        !hasRequiredDocuments(getSuggestedOffer(refetchedOffersData.offers))
      ) {
        return goToApplication();
      }
    } catch {
      handleSignupError();
    } finally {
      setIsDeciding(false);
    }
  };

  // Handle errors once data is stable
  useEffect(
    () => {
      if (isLoadingOffers || isRefetchingOffers || stateFetching) return;

      if (offersData?.status === 500 || offersError) {
        handleSignupError();
        return;
      }

      if (offers) {
        handleSetSignupErrors(offers);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      offers,
      offersData?.status,
      offersError,
      isLoadingOffers,
      isRefetchingOffers,
      stateFetching,
    ],
  );

  return {
    applicationId,
    documentRequirements,
    refetchOffersData,
    isRefetchingOffers,
    isLoadingOffers,
    isLoadingState: !state,
    dataRequirementErrors,
    offers,
    hasIndicativeOffers,
    handleDecision,
    isErrorChecked,
    setIsErrorChecked,
    isSecurityCheckRequired,
    shouldRedirect,
    handleRedirect,
    hasSignupDecision:
      hasSignupDecision || hasIndicativeOffers || hasSampleOffers,
    signupError,
    selectOfferError,
    setSelectOfferError,
    isDeciding,
    autoBankStatements: getAutoBankStatements(offers),
    isReintroduction,
  };
};
